import React, { useEffect } from 'react';
import Nav from '../../components/Nav/Nav';

import { UserContext } from '../../App';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';

import './NewTicketView.scss';
import { Link, useSearchParams } from 'react-router-dom';
import GenderPronounFields from '../../components/GenderPronounFields/GenderPronounFields';
import AuthApi from '../../api/AuthApi';
import { FullUserDto } from '../../interfaces/UserInterfaces';
import {
  isCourtSelected,
  isUserProfileComplete,
  getUserProfileMissingFields,
} from '../../utils/intakeValidation';
import {
  isGenderPronounValid,
  PronounSet,
  TicketGender,
} from '../../utils/ticketGenderPronouns';
import UploadTicket, {
  ParsedTicketData,
} from '../../components/UploadTicket/UploadTicket';
import TicketOcrVerifyDialog from '../../components/TicketOcrVerifyDialog/TicketOcrVerifyDialog';
import { applyParsedTicketData } from '../../utils/applyParsedTicketData';
import ProsecutorSelect from '../../components/ProsecutorSelect/ProsecutorSelect';
import CourtSelect from '../../components/CourtSelect/CourtSelect';
import { FullProsecutorDto } from '../../api/ProsecutorApi';
import { FullCourtDto } from '../../api/CourtApi';
import ProductApi from '../../api/ProductApi';
import { ProductDto } from '../../interfaces/ProductInterfaces';
import TicketApi, { TicketPricingDto } from '../../api/TicketApi';
import { parseApiErrorMessage } from '../../utils/parseApiError';
import {
  productMatchesTicketLicenseType,
  ticketLicenseTypeToCode,
  urlTypeParamToTicketValue,
} from '../../utils/productLicenseType';

function getSubmitErrorHint(message: string): string | null {
  const lower = message.toLowerCase();
  if (lower.includes('court date')) {
    return 'Go back to Enter Details and set a future court date, or clear the field.';
  }
  if (lower.includes('license type')) {
    return 'Go back to Enter Details and choose a license type that matches your plan.';
  }
  if (
    lower.includes('prosecutor not found') ||
    lower.includes('court not found')
  ) {
    return 'Go back to Enter Details and pick a different court or prosecutor.';
  }
  return null;
}

const NewTicketView = () => {
  const user = React.useContext(UserContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeStep, setActiveStep] = React.useState(0);
  const [uploadCompleted, setUploadCompleted] = React.useState(false);
  const [uploadedDocumentGuid, setUploadedDocumentGuid] = React.useState<
    string | null
  >(null);
  const [uploadedViaImage, setUploadedViaImage] = React.useState(false);
  const [ocrParseSucceeded, setOcrParseSucceeded] = React.useState(true);
  const [ocrVerifyModalOpen, setOcrVerifyModalOpen] = React.useState(false);
  const [ocrVerifyAcknowledged, setOcrVerifyAcknowledged] =
    React.useState(false);

  const [canContinue, setCanContinue] = React.useState(false);
  const [productsError, setProductsError] = React.useState<string | null>(null);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [paymentCancelledMessage, setPaymentCancelledMessage] = React.useState<
    string | null
  >(null);

  const [ticketNumber, setTicketNumber] = React.useState('');
  const [ticketCitation, setTicketCitation] = React.useState('');
  const [ticketAddress, setTicketAddress] = React.useState('');
  const [ticketCity, setTicketCity] = React.useState('');
  const [ticketCounty, setTicketCounty] = React.useState('');
  const [ticketCourtDate, setTicketCourtDate] = React.useState('');
  const [ticketLicenseType, setTicketLicenseType] = React.useState('');
  // Filled from the citation by the parser, never typed here. They have no field on this form
  // because the case page is where they are read and corrected; carrying them means a preservation
  // demand can name the agency that holds the footage instead of "Custodian of Records".
  const [ticketArrestingAgency, setTicketArrestingAgency] = React.useState('');
  const [ticketAgencyCaseNumber, setTicketAgencyCaseNumber] = React.useState('');
  const [ticketLicensePlate, setTicketLicensePlate] = React.useState('');
  const [ticketZone, setTicketZone] = React.useState('');
  const [ticketUrl, setTicketUrl] = React.useState('');
  const [driversLicenseNumber, setDriversLicenseNumber] = React.useState('');
  const [ticketGender, setTicketGender] = React.useState<TicketGender>('');
  const [ticketPronounSet, setTicketPronounSet] = React.useState<PronounSet>('');
  const [profileIncomplete, setProfileIncomplete] = React.useState<
    string | null
  >(null);
  const [hasTakenDscInPastYear, setHasTakenDscInPastYear] =
    React.useState(false);
  const [coverage, setCoverage] = React.useState('');
  const [selectedProsecutor, setSelectedProsecutor] =
    React.useState<FullProsecutorDto | null>(null);
  const [selectedCourt, setSelectedCourt] = React.useState<FullCourtDto | null>(
    null,
  );
  const [products, setProducts] = React.useState<ProductDto[]>([]);
  const [loadingProducts, setLoadingProducts] = React.useState(false);
  const [pricingInfo, setPricingInfo] = React.useState<TicketPricingDto | null>(
    null,
  );
  const [loadingPricing, setLoadingPricing] = React.useState(false);
  const [pricingError, setPricingError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (searchParams.get('payment_cancelled') === 'true') {
      setPaymentCancelledMessage(
        'Payment was cancelled. You can review your details and try again.',
      );
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('payment_cancelled');
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  React.useEffect(() => {
    const fetchProfile = async () => {
      try {
        const userData = await AuthApi.me();
        const isFullUser = (obj: unknown): obj is FullUserDto =>
          typeof obj === 'object' &&
          obj !== null &&
          'User_guid' in obj &&
          !('error' in obj);
        if (!isFullUser(userData)) return;
        if (!isUserProfileComplete(userData)) {
          const missing = getUserProfileMissingFields(userData);
          setProfileIncomplete(
            `Please complete your profile before creating a ticket (missing: ${missing.join(', ')}).`,
          );
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };
    fetchProfile();
  }, []);

  // Fetch products on component mount
  React.useEffect(() => {
    const fetchProducts = async () => {
      setLoadingProducts(true);
      setProductsError(null);
      try {
        const result = await ProductApi.getProducts({ from: 0, to: 100 });
        setProducts(result.data);
      } catch (error) {
        console.error('Error fetching products:', error);
        setProductsError(
          error instanceof Error
            ? error.message
            : 'Failed to load pricing options',
        );
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchProducts();
  }, []);

  // Check for ticket preference URL parameters and prefill form fields
  const preferredTypeApplied = React.useRef(false);
  React.useEffect(() => {
    if (products.length === 0 || preferredTypeApplied.current) return;

    const preferredType = localStorage.getItem('preferredTicketType');
    const ticketValue = urlTypeParamToTicketValue(preferredType);

    if (ticketValue) {
      setTicketLicenseType(ticketValue);
    }
    preferredTypeApplied.current = true;
  }, [products]);

  // Clear selected coverage if it no longer matches the ticket license type
  React.useEffect(() => {
    if (!coverage) return;

    const selectedProduct = products.find(
      (product) => product.Product_Name === coverage,
    );
    if (
      selectedProduct &&
      !productMatchesTicketLicenseType(
        selectedProduct.Product_LicenseType,
        ticketLicenseType,
      )
    ) {
      setCoverage('');
      setPricingInfo(null);
    }
  }, [ticketLicenseType, products, coverage]);

  const matchingProducts = React.useMemo(
    () =>
      products.filter((product) =>
        productMatchesTicketLicenseType(
          product.Product_LicenseType,
          ticketLicenseType,
        ),
      ),
    [products, ticketLicenseType],
  );

  // Fetch pricing when coverage changes
  React.useEffect(() => {
    const fetchPricing = async () => {
      if (coverage && coverage.trim()) {
        setLoadingPricing(true);
        setPricingError(null);
        try {
          const pricing = await TicketApi.getPricing(coverage);
          setPricingInfo(pricing);
        } catch (error) {
          console.error('Error fetching pricing:', error);
          setPricingInfo(null);
          setPricingError(
            error instanceof Error
              ? error.message
              : 'Unable to load pricing information',
          );
        } finally {
          setLoadingPricing(false);
        }
      } else {
        setPricingInfo(null);
        setPricingError(null);
      }
    };

    fetchPricing();
  }, [coverage]);

  useEffect(() => {
    if (profileIncomplete) {
      setCanContinue(false);
      return;
    }
    // Step-aware validation logic
    if (activeStep === 0) {
      // For step 0 (upload), continue is enabled after upload completion
      setCanContinue(uploadCompleted);
    } else if (activeStep === 1) {
      // For step 1 (details), continue is enabled when all required fields are filled
      setCanContinue(ticketInfoValid());
    } else {
      // For step 2 (coverage), require a selected plan with loaded pricing
      setCanContinue(
        coverage.length > 0 &&
          !loadingPricing &&
          !!pricingInfo &&
          !productsError,
      );
    }
  }, [
    activeStep,
    uploadCompleted,
    ticketNumber,
    ticketAddress,
    ticketCitation,
    ticketCity,
    ticketCounty,
    ticketCourtDate,
    ticketLicenseType,
    ticketZone,
    coverage,
    loadingPricing,
    pricingInfo,
    productsError,
    ticketGender,
    ticketPronounSet,
    selectedCourt,
    profileIncomplete,
  ]);

  useEffect(() => {
    if (activeStep === 1 && uploadedViaImage && !ocrVerifyAcknowledged) {
      setOcrVerifyModalOpen(true);
    }
  }, [activeStep, uploadedViaImage, ocrVerifyAcknowledged]);

  const handleTicketUploaded = (
    documentId: string,
    parsedData?: ParsedTicketData,
    meta?: { ocrSucceeded: boolean },
  ) => {
    console.log('Ticket uploaded:', documentId, parsedData);

    if (parsedData) {
      applyParsedTicketData(parsedData, {
        setTicketNumber,
        setTicketCitation,
        setTicketAddress,
        setTicketCity,
        setTicketCounty,
        setTicketCourtDate,
        setTicketLicenseType,
        setTicketArrestingAgency,
        setTicketAgencyCaseNumber,
        setTicketLicensePlate,
      });
    }

    setUploadedDocumentGuid(documentId.trim() || null);
    setUploadedViaImage(true);
    setOcrParseSucceeded(meta?.ocrSucceeded ?? true);
    setOcrVerifyAcknowledged(false);
    setUploadCompleted(true);
    setActiveStep(1);
  };

  const handleGenderPronounChange = (
    gender: TicketGender,
    pronounSet: PronounSet,
  ) => {
    setTicketGender(gender);
    setTicketPronounSet(pronounSet);
  };

  const ticketInfoValid = () => {
    return (
      isCourtSelected(selectedCourt?.Court_guid) &&
      isGenderPronounValid(ticketGender, ticketPronounSet)
    );
  };

  const createTicketReq = async () => {
    setSubmitting(true);
    setSubmitError(null);

    try {
      const { checkout_url } = await TicketApi.createPaymentSession({
        Ticket_Number: ticketNumber,
        Ticket_Representation: coverage,
        Ticket_Citation: ticketCitation,
        Ticket_Address: ticketAddress,
        Ticket_City: ticketCity,
        Ticket_County: ticketCounty,
        Ticket_CourtDate: ticketCourtDate,
        Ticket_LicenseType: ticketLicenseType,
        Ticket_ArrestingAgency: ticketArrestingAgency || undefined,
        Ticket_AgencyCaseNumber: ticketAgencyCaseNumber || undefined,
        Ticket_LicensePlate: ticketLicensePlate || undefined,
        Ticket_Zone: ticketZone,
        Ticket_Url: ticketUrl,
        Prosecutor_guid: selectedProsecutor?.Prosecutor_guid,
        Court_guid: selectedCourt!.Court_guid,
        Ticket_DriversLicenseNumber: driversLicenseNumber.trim() || undefined,
        Ticket_Gender: ticketGender || undefined,
        Ticket_PronounSet: ticketPronounSet || undefined,
        Ticket_HasTakenDscInPastYear: hasTakenDscInPastYear,
        ...(uploadedDocumentGuid
          ? { UserDocument_guid: uploadedDocumentGuid }
          : {}),
      });

      if (checkout_url) {
        window.location.href = checkout_url;
        return;
      }

      throw new Error('No checkout URL received from server');
    } catch (error) {
      console.error('Error creating ticket payment:', error);
      const message =
        error instanceof Error && error.message
          ? error.message
          : parseApiErrorMessage(null);
      setSubmitError(message);
      setSubmitting(false);
    }
  };

  const submitErrorHint = submitError ? getSubmitErrorHint(submitError) : null;

  return (
    <>
      <TicketOcrVerifyDialog
        open={ocrVerifyModalOpen}
        onClose={() => {
          setOcrVerifyModalOpen(false);
          setOcrVerifyAcknowledged(true);
        }}
        ocrSucceeded={ocrParseSucceeded}
      />
      <Nav></Nav>
      <div className="ntickv">
        <Container className="ntickv-ctn">
          <Typography variant="h1" className="ntickv-ctn-ttl">
            New Ticket
          </Typography>
          {profileIncomplete && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {profileIncomplete}{' '}
              <Link to="/settings">Update your profile in settings</Link>
            </Alert>
          )}
          {paymentCancelledMessage && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              onClose={() => setPaymentCancelledMessage(null)}
            >
              {paymentCancelledMessage}
            </Alert>
          )}
          <div className="ntickv-ctn-stepper">
            <Stepper activeStep={activeStep}>
              {[0, 1, 2].map((step) => (
                <Step key={step}>
                  <StepLabel className="ntickv-ctn-stepper-lbl">
                    {step === 0
                      ? 'Upload Ticket'
                      : step === 1
                        ? 'Enter Details'
                        : step === 2
                          ? 'Review & Submit'
                          : 'Confirmation'}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </div>
          <br />
          {activeStep === 0 && (
            <>
              <Typography>
                First, upload a photo or PDF of your ticket.
              </Typography>
              <UploadTicket uploaded={handleTicketUploaded}></UploadTicket>
              <br />
              <Typography
                variant="body2"
                style={{ marginTop: '16px', textAlign: 'center' }}
              >
                or{' '}
                <Button
                  variant="text"
                  onClick={() => {
                    setUploadedViaImage(false);
                    setUploadedDocumentGuid(null);
                    setUploadCompleted(true);
                  }}
                  style={{ textTransform: 'none' }}
                >
                  skip upload and enter details manually
                </Button>
              </Typography>
            </>
          )}
          {activeStep === 1 && (
            <>
              <Typography>Edit your ticket details</Typography>
              {/* Make a form for all the ticket fields */}
              <div className="ntickv-ctn-form">
                <TextField
                  label="Ticket Number (Optional)"
                  value={ticketNumber}
                  onChange={(e) => setTicketNumber(e.target.value)}
                />
                <TextField
                  label="Ticket Citation (Optional)"
                  placeholder="e.g. Failure to drive in single lane"
                  value={ticketCitation}
                  onChange={(e) => setTicketCitation(e.target.value)}
                />
                <TextField
                  label="Violation Address (Optional)"
                  value={ticketAddress}
                  onChange={(e) => setTicketAddress(e.target.value)}
                />
                <TextField
                  label="Violation City (Optional)"
                  value={ticketCity}
                  onChange={(e) => setTicketCity(e.target.value)}
                />
                <TextField
                  label="Violation County (Optional)"
                  value={ticketCounty}
                  onChange={(e) => setTicketCounty(e.target.value)}
                />
                <TextField
                  label="Court Date (Optional)"
                  type="date"
                  value={ticketCourtDate}
                  onChange={(e) => setTicketCourtDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <TextField
                  label="Driver's License # (Optional)"
                  value={driversLicenseNumber}
                  onChange={(e) => setDriversLicenseNumber(e.target.value)}
                />
                <GenderPronounFields
                  gender={ticketGender}
                  pronounSet={ticketPronounSet}
                  onGenderChange={handleGenderPronounChange}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hasTakenDscInPastYear}
                      onChange={(e) =>
                        setHasTakenDscInPastYear(e.target.checked)
                      }
                    />
                  }
                  label="Has taken a driver safety course in the past year"
                />
                <FormControl fullWidth>
                  <InputLabel id="ticket-license-type-label">
                    License Type (Optional)
                  </InputLabel>
                  <Select
                    value={ticketLicenseType}
                    label="License Type (Optional)"
                    onChange={(e) => setTicketLicenseType(e.target.value)}
                  >
                    <MenuItem value="">Not Specified</MenuItem>
                    <MenuItem value="Class C">Class C</MenuItem>
                    <MenuItem value="CDL">CDL</MenuItem>
                    <MenuItem value="None">No License</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel id="ticket-zone-label">
                    License Zone (Optional)
                  </InputLabel>
                  <Select
                    value={ticketZone}
                    label="License Zone (Optional)"
                    onChange={(e) => setTicketZone(e.target.value)}
                  >
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="Street">Street</MenuItem>
                    <MenuItem value="Highway">Highway</MenuItem>
                    <MenuItem value="Interstate">Interstate</MenuItem>
                    <MenuItem value="Parking">Parking</MenuItem>
                    <MenuItem value="School Zone">School Zone</MenuItem>
                    <MenuItem value="Construction Zone">
                      Construction Zone
                    </MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
                <CourtSelect
                  value={selectedCourt}
                  onChange={(court) => {
                    setSelectedCourt(court);
                    // Reset prosecutor when court changes
                    if (
                      selectedProsecutor &&
                      selectedProsecutor.court?.Court_guid !== court?.Court_guid
                    ) {
                      setSelectedProsecutor(null);
                    }
                  }}
                  label="Court"
                  placeholder="Search and select a court..."
                  required
                />
                <ProsecutorSelect
                  value={selectedProsecutor}
                  onChange={setSelectedProsecutor}
                  label="Prosecutor (Optional)"
                  placeholder="Search and select a prosecutor..."
                  disabled={!selectedCourt}
                  courtGuidFilter={selectedCourt?.Court_guid}
                  helperText={
                    !selectedCourt ? 'Please select a court first' : undefined
                  }
                />
              </div>
            </>
          )}
          {activeStep === 2 && (
            <>
              {ticketLicenseType &&
                ticketLicenseTypeToCode(ticketLicenseType) && (
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={`Plans for: ${ticketLicenseType}`}
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                )}
              <Typography>Select your level of care</Typography>
              {productsError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {productsError}
                </Alert>
              )}
              {!loadingProducts &&
                !productsError &&
                ticketLicenseType &&
                matchingProducts.length === 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    No plans are available for {ticketLicenseType}. Go back and
                    choose a different license type, or contact support.
                  </Alert>
                )}
              <Select
                value={coverage}
                label="Coverage"
                onChange={(e) => {
                  setCoverage(e.target.value);
                  setSubmitError(null);
                }}
                className="ntickv-ctn-coverage-select"
                disabled={
                  loadingProducts ||
                  !!productsError ||
                  matchingProducts.length === 0
                }
              >
                {loadingProducts ? (
                  <MenuItem disabled>Loading pricing options...</MenuItem>
                ) : productsError ? (
                  <MenuItem disabled>Unable to load pricing options</MenuItem>
                ) : matchingProducts.length === 0 ? (
                  <MenuItem disabled>No matching plans available</MenuItem>
                ) : (
                  matchingProducts.map((product) => (
                    <MenuItem
                      key={product.Product_guid}
                      value={product.Product_Name}
                    >
                      {product.Product_Name} — $
                      {(product.Product_Cost / 100).toFixed(2)}
                    </MenuItem>
                  ))
                )}
              </Select>
            </>
          )}

          {/* Pricing Breakdown */}
          {coverage && (
            <div
              style={{
                marginTop: '16px',
                padding: '16px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
              }}
            >
              <Typography variant="h6" gutterBottom>
                Pricing Breakdown
              </Typography>
              {loadingPricing ? (
                <Typography>Loading pricing...</Typography>
              ) : pricingInfo ? (
                <div>
                  <Typography>
                    Base Cost: {pricingInfo.baseCostFormatted}
                  </Typography>
                  {pricingInfo.isUnderage && (
                    <>
                      <Typography
                        color="warning.main"
                        style={{ fontWeight: 'bold' }}
                      >
                        Underage Surcharge:{' '}
                        {pricingInfo.underageSurchargeFormatted}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Additional fee for users under 18 years old (Your age:{' '}
                        {pricingInfo.userAge})
                      </Typography>
                    </>
                  )}
                  <Typography
                    variant="h6"
                    style={{ marginTop: '8px', fontWeight: 'bold' }}
                  >
                    Total: {pricingInfo.totalCostFormatted}
                  </Typography>
                </div>
              ) : pricingError ? (
                <Typography color="error">{pricingError}</Typography>
              ) : (
                <Typography color="error">
                  Unable to load pricing information
                </Typography>
              )}
            </div>
          )}
          {submitError && (
            <Alert
              severity="error"
              sx={{ mt: 2 }}
              onClose={() => setSubmitError(null)}
            >
              <Typography component="span" display="block">
                {submitError}
              </Typography>
              {submitErrorHint && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {submitErrorHint}
                </Typography>
              )}
            </Alert>
          )}
          <div className="ntickv-ctn-actns">
            <Button
              disabled={activeStep == 0 || submitting}
              variant="contained"
              color="inherit"
              onClick={() => {
                const newStep = activeStep - 1;
                setActiveStep(newStep);
                setSubmitError(null);
                // Reset upload completion if going back to step 0
                if (newStep === 0) {
                  setUploadCompleted(false);
                  setUploadedViaImage(false);
                  setUploadedDocumentGuid(null);
                }
              }}
            >
              Back
            </Button>
            <Button
              disabled={!canContinue || loadingPricing || submitting}
              variant="contained"
              color="primary"
              onClick={() => {
                if (activeStep === 2) {
                  createTicketReq();
                } else {
                  setSubmitError(null);
                  setActiveStep(activeStep + 1);
                }
              }}
            >
              {activeStep === 2
                ? submitting
                  ? 'Redirecting to payment...'
                  : 'Submit Ticket'
                : 'Continue'}
            </Button>
          </div>
        </Container>
      </div>
    </>
  );
};
export default NewTicketView;
